import React, { useState } from "react";
import { motion, LayoutGroup } from "framer-motion";
import { Opportunity, OpportunityStage } from "../../types";
import {
  Calendar,
  User,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  RotateCcw,
  ExternalLink,
  Clock,
  FileText,
  Rows3,
  SlidersHorizontal,
  CheckCircle,
  AlertCircle,
  Layers,
} from "lucide-react";

export type KanbanCardViewMode = "standard" | "detailed";

export interface PipelineKanbanProps {
  opportunities: Opportunity[];
  onOpenQualify: (opp: Opportunity) => void;
  onOpenEdit: (opp: Opportunity) => void;
  onAdvanceStage: (opp: Opportunity, nextStage: OpportunityStage) => void;
  onTransitionStage?: (opp: Opportunity, newStage: OpportunityStage) => void;
  onOpenWorkspace?: (opp: Opportunity) => void;
  cardViewMode?: KanbanCardViewMode;
  onCardViewModeChange?: (mode: KanbanCardViewMode) => void;
}

interface ColumnConfig {
  id: string;
  title: string;
  stages: OpportunityStage[];
  color: string;
  accentBg: string;
}

const COLUMNS: ColumnConfig[] = [
  {
    id: "intake",
    title: "Intake & Reviewing",
    stages: ["reviewing"],
    color: "border-amber-400 text-amber-600 dark:text-amber-400",
    accentBg: "bg-amber-50 dark:bg-amber-950/30",
  },
  {
    id: "drafting",
    title: "Qualified & Drafting",
    stages: ["go", "drafting"],
    color: "border-teal-500 text-[#2ba5b5] dark:text-teal-400",
    accentBg: "bg-teal-50 dark:bg-teal-950/30",
  },
  {
    id: "in_review",
    title: "Review & Approvals",
    stages: ["in_review", "approved"],
    color: "border-purple-500 text-purple-600 dark:text-purple-400",
    accentBg: "bg-purple-50 dark:bg-purple-950/30",
  },
  {
    id: "submitted",
    title: "Submitted",
    stages: ["submitted"],
    color: "border-cyan-500 text-cyan-600 dark:text-cyan-400",
    accentBg: "bg-cyan-50 dark:bg-cyan-950/30",
  },
  {
    id: "outcomes",
    title: "Won / Lost / Declined",
    stages: ["won", "lost", "no_go", "archived"],
    color: "border-slate-400 text-slate-600 dark:text-slate-400",
    accentBg: "bg-slate-100 dark:bg-slate-900/50",
  },
];

const getStageBadgeStyle = (stage: OpportunityStage): string => {
  switch (stage) {
    case "reviewing":
      return "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300";
    case "go":
      return "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300";
    case "drafting":
      return "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300";
    case "in_review":
      return "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300";
    case "approved":
      return "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300";
    case "submitted":
      return "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/60 dark:text-cyan-300";
    case "won":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300";
    case "lost":
      return "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300";
    case "no_go":
    case "archived":
    default:
      return "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-400";
  }
};

export const PipelineKanban: React.FC<PipelineKanbanProps> = ({
  opportunities,
  onOpenQualify,
  onOpenEdit,
  onAdvanceStage,
  onTransitionStage,
  onOpenWorkspace,
  cardViewMode: controlledCardViewMode,
  onCardViewModeChange,
}) => {
  // Local card view state if not controlled externally
  const [internalViewMode, setInternalViewMode] = useState<KanbanCardViewMode>(() => {
    try {
      return (localStorage.getItem("phq_kanban_card_view") as KanbanCardViewMode) || "standard";
    } catch {
      return "standard";
    }
  });

  const [activeDropColumn, setActiveDropColumn] = useState<string | null>(null);
  const [draggedOppId, setDraggedOppId] = useState<string | null>(null);

  const viewMode = controlledCardViewMode ?? internalViewMode;

  const handleToggleViewMode = (mode: KanbanCardViewMode) => {
    if (onCardViewModeChange) {
      onCardViewModeChange(mode);
    } else {
      setInternalViewMode(mode);
      try {
        localStorage.setItem("phq_kanban_card_view", mode);
      } catch (err) {
        console.warn("Failed to persist kanban view mode:", err);
      }
    }
  };

  const transition = onTransitionStage || onAdvanceStage;

  const handleOpenWorkspace = (opp: Opportunity) => {
    if (onOpenWorkspace) {
      onOpenWorkspace(opp);
    } else if (typeof window !== "undefined") {
      window.location.assign(`/opportunities/${opp.id}`);
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, opp: Opportunity) => {
    e.dataTransfer.setData("text/plain", opp.id);
    e.dataTransfer.effectAllowed = "move";
    setDraggedOppId(opp.id);
  };

  const handleDragEnd = () => {
    setDraggedOppId(null);
    setActiveDropColumn(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, colId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (activeDropColumn !== colId) {
      setActiveDropColumn(colId);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>, colId: string) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    if (activeDropColumn === colId) {
      setActiveDropColumn(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, col: ColumnConfig) => {
    e.preventDefault();
    setActiveDropColumn(null);
    const oppId = e.dataTransfer.getData("text/plain") || draggedOppId;
    if (!oppId) return;

    const opp = opportunities.find((o) => o.id === oppId);
    if (!opp) return;

    let targetStage: OpportunityStage;
    if (col.id === "intake") {
      targetStage = "reviewing";
    } else if (col.id === "drafting") {
      targetStage = opp.stage === "go" ? "go" : "drafting";
    } else if (col.id === "in_review") {
      targetStage = "in_review";
    } else if (col.id === "submitted") {
      targetStage = "submitted";
    } else {
      targetStage =
        opp.stage === "lost"
          ? "lost"
          : opp.stage === "no_go"
            ? "no_go"
            : opp.stage === "archived"
              ? "archived"
              : "won";
    }

    if (opp.stage !== targetStage) {
      transition(opp, targetStage);
    }
  };

  return (
    <div className="space-y-4">
      {/* Kanban Board Density & Status Toolbar */}
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200/80 bg-white/70 px-4 py-3 shadow-2xs sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 dark:bg-slate-900/60">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Board Density:
            </span>
            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
              {viewMode === "detailed" ? "Detailed View" : "Standard View"}
            </span>
          </div>
          <span className="hidden text-slate-300 sm:inline dark:text-slate-700">•</span>
          <span className="hidden text-xs text-slate-500 sm:inline dark:text-slate-400">
            {opportunities.length} Total RFPs • Drag cards to advance stages
          </span>
        </div>

        {/* Standard vs Detailed Toggle */}
        <div className="flex items-center rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
          <button
            type="button"
            onClick={() => handleToggleViewMode("standard")}
            title="Standard Kanban View"
            aria-pressed={viewMode === "standard"}
            className={`btn-animate inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition-all ${
              viewMode === "standard"
                ? "bg-white text-[#2ba5b5] shadow-2xs dark:bg-slate-700 dark:text-teal-400"
                : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
            }`}
          >
            <Rows3 className="h-3.5 w-3.5" />
            <span>Standard</span>
          </button>
          <button
            type="button"
            onClick={() => handleToggleViewMode("detailed")}
            title="Detailed Kanban View"
            aria-pressed={viewMode === "detailed"}
            className={`btn-animate inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition-all ${
              viewMode === "detailed"
                ? "bg-white text-[#2ba5b5] shadow-2xs dark:bg-slate-700 dark:text-teal-400"
                : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
            }`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span>Detailed</span>
          </button>
        </div>
      </div>

      {/* Board Columns Grid */}
      <LayoutGroup id="pipeline-kanban">
        <div className="grid grid-cols-1 items-start gap-4 overflow-x-auto pb-4 lg:grid-cols-5">
          {COLUMNS.map((col) => {
            const columnOpps = opportunities.filter((o) => col.stages.includes(o.stage));
            const isDropTarget = activeDropColumn === col.id;

            return (
              <div
                key={col.id}
                onDragOver={(e) => handleDragOver(e, col.id)}
                onDragLeave={(e) => handleDragLeave(e, col.id)}
                onDrop={(e) => handleDrop(e, col)}
                className={`flex min-w-[240px] flex-col rounded-xl border p-3.5 shadow-2xs transition-colors duration-150 ${
                  isDropTarget
                    ? "border-[#2ba5b5] bg-teal-50/70 ring-2 ring-[#2ba5b5]/30 dark:bg-teal-950/40"
                    : "border-slate-200/80 bg-slate-50/70 dark:border-slate-800/80 dark:bg-slate-900/60"
                }`}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between border-b border-slate-200/60 pb-2.5 dark:border-slate-800/60">
                  <h3 className={`text-xs font-bold uppercase tracking-wider ${col.color}`}>
                    {col.title}
                  </h3>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-slate-700 shadow-2xs dark:bg-slate-800 dark:text-slate-300">
                    {columnOpps.length}
                  </span>
                </div>

                {/* Column Cards with Framer Motion Layout Animations */}
                <div className="mt-3 space-y-3">
                  {columnOpps.length === 0 ? (
                    <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-slate-300/80 p-2 text-center text-xs text-slate-400 dark:border-slate-800">
                      No active bids
                    </div>
                  ) : (
                    columnOpps.map((opp) => (
                      <motion.div
                        layout
                        layoutId={`kanban-card-${opp.id}`}
                        transition={{
                          type: "spring",
                          stiffness: 350,
                          damping: 28,
                          mass: 0.8,
                        }}
                        key={opp.id}
                        draggable
                        onDragStartCapture={(e) => handleDragStart(e, opp)}
                        onDragEndCapture={handleDragEnd}
                        onClick={() => onOpenEdit(opp)}
                        className={`group cursor-pointer rounded-xl border bg-white shadow-2xs transition-all hover:border-[#2ba5b5] hover:shadow-md dark:bg-slate-800/90 ${
                          draggedOppId === opp.id
                            ? "opacity-50 ring-2 ring-[#2ba5b5]"
                            : "border-slate-200/90 dark:border-slate-800"
                        } ${viewMode === "detailed" ? "p-4 space-y-3" : "p-3 space-y-2.5"}`}
                      >
                        {/* ================= STANDARD VIEW ================= */}
                        {viewMode === "standard" ? (
                          <>
                            <div className="flex items-start justify-between gap-1">
                              <span className="max-w-[150px] truncate text-[11px] font-bold text-[#2ba5b5]">
                                {opp.client}
                              </span>
                              <span
                                className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${getStageBadgeStyle(
                                  opp.stage,
                                )}`}
                              >
                                {opp.stage.replace("_", " ")}
                              </span>
                            </div>

                            <h4 className="line-clamp-2 text-xs font-semibold text-slate-800 dark:text-slate-100">
                              {opp.title}
                            </h4>

                            <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-[11px] text-slate-500 dark:border-slate-700/60">
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3 text-slate-400" />
                                <span>{opp.owner}</span>
                              </span>
                              <span className="flex items-center gap-1 font-medium">
                                <Calendar className="h-3 w-3 text-slate-400" />
                                <span>{opp.dueDate}</span>
                              </span>
                            </div>

                            {/* Quick Action Footer */}
                            <div className="flex items-center justify-between pt-1">
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenQualify(opp);
                                  }}
                                  className="rounded px-2 py-0.5 text-[10px] font-bold text-[#2ba5b5] hover:bg-teal-50 dark:hover:bg-teal-950/60"
                                >
                                  {opp.decision
                                    ? `Decision: ${opp.decision.toUpperCase()}`
                                    : "Qualify"}
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenWorkspace(opp);
                                  }}
                                  title="Open Opportunity Workspace"
                                  aria-label="Open Workspace"
                                  className="btn-animate inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:border-[#2ba5b5] hover:text-[#2ba5b5] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-teal-400"
                                >
                                  <ExternalLink className="h-2.5 w-2.5" />
                                  <span>Workspace</span>
                                </button>
                              </div>

                              <div className="flex items-center gap-1">
                                {/* Move Back Button */}
                                {opp.stage === "drafting" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "reviewing");
                                    }}
                                    title="Move back to Intake"
                                    className="btn-animate inline-flex items-center rounded border border-slate-200 bg-slate-50 p-1 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <ArrowLeft className="h-2.5 w-2.5" />
                                  </button>
                                )}
                                {opp.stage === "in_review" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "drafting");
                                    }}
                                    title="Move back to Drafting"
                                    className="btn-animate inline-flex items-center rounded border border-slate-200 bg-slate-50 p-1 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <ArrowLeft className="h-2.5 w-2.5" />
                                  </button>
                                )}
                                {opp.stage === "approved" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "in_review");
                                    }}
                                    title="Move back to Review"
                                    className="btn-animate inline-flex items-center rounded border border-slate-200 bg-slate-50 p-1 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <ArrowLeft className="h-2.5 w-2.5" />
                                  </button>
                                )}
                                {opp.stage === "submitted" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "approved");
                                    }}
                                    title="Move back to Approved"
                                    className="btn-animate inline-flex items-center rounded border border-slate-200 bg-slate-50 p-1 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <ArrowLeft className="h-2.5 w-2.5" />
                                  </button>
                                )}

                                {/* Move Forward Button */}
                                {opp.stage === "reviewing" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "drafting");
                                    }}
                                    title="Advance to drafting"
                                    className="btn-animate inline-flex items-center gap-1 rounded bg-[#2ba5b5] px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-[#238997]"
                                  >
                                    <span>Draft</span>
                                    <ArrowRight className="h-2.5 w-2.5" />
                                  </button>
                                )}

                                {opp.stage === "go" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "drafting");
                                    }}
                                    title="Start drafting"
                                    className="btn-animate inline-flex items-center gap-1 rounded bg-teal-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-teal-700"
                                  >
                                    <span>Draft</span>
                                    <ArrowRight className="h-2.5 w-2.5" />
                                  </button>
                                )}

                                {opp.stage === "drafting" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "in_review");
                                    }}
                                    title="Advance to review"
                                    className="btn-animate inline-flex items-center gap-1 rounded bg-purple-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-purple-700"
                                  >
                                    <span>Review</span>
                                    <ArrowRight className="h-2.5 w-2.5" />
                                  </button>
                                )}

                                {opp.stage === "in_review" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "approved");
                                    }}
                                    title="Approve"
                                    className="btn-animate inline-flex items-center gap-1 rounded bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-indigo-700"
                                  >
                                    <span>Approve</span>
                                    <CheckCircle2 className="h-2.5 w-2.5" />
                                  </button>
                                )}

                                {opp.stage === "approved" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "submitted");
                                    }}
                                    title="Mark submitted"
                                    className="btn-animate inline-flex items-center gap-1 rounded bg-cyan-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-cyan-700"
                                  >
                                    <span>Submit</span>
                                    <CheckCircle2 className="h-2.5 w-2.5" />
                                  </button>
                                )}

                                {["won", "lost", "no_go", "archived"].includes(opp.stage) && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "drafting");
                                    }}
                                    title="Reopen opportunity back to drafting"
                                    className="btn-animate inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <RotateCcw className="h-2.5 w-2.5 text-slate-400" />
                                    <span>Reopen</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        ) : (
                          /* ================= DETAILED VIEW ================= */
                          <>
                            {/* Detailed Header */}
                            <div className="flex items-start justify-between gap-2">
                              <span className="truncate text-xs font-bold text-[#2ba5b5]">
                                {opp.client}
                              </span>
                              <div className="flex items-center gap-1.5">
                                {opp.decision === "go" && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold text-teal-800 dark:bg-teal-950/60 dark:text-teal-300">
                                    <CheckCircle className="h-2.5 w-2.5" />
                                    GO
                                  </span>
                                )}
                                {opp.decision === "no_go" && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-800 dark:bg-rose-950/60 dark:text-rose-300">
                                    <AlertCircle className="h-2.5 w-2.5" />
                                    NO-GO
                                  </span>
                                )}
                                <span
                                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${getStageBadgeStyle(
                                    opp.stage,
                                  )}`}
                                >
                                  {opp.stage.replace("_", " ")}
                                </span>
                              </div>
                            </div>

                            {/* Detailed Title */}
                            <h4 className="line-clamp-2 text-sm font-bold text-slate-900 dark:text-slate-100">
                              {opp.title}
                            </h4>

                            {/* Services Tags */}
                            {opp.services && opp.services.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {opp.services.map((svc) => (
                                  <span
                                    key={svc}
                                    className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-700/60 dark:text-slate-300"
                                  >
                                    {svc}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Detailed Information Grid */}
                            <div className="space-y-1.5 rounded-lg border border-slate-100 bg-slate-50/60 p-2.5 text-xs text-slate-600 dark:border-slate-700/50 dark:bg-slate-900/40 dark:text-slate-300">
                              <div className="flex items-center justify-between">
                                <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                                  <User className="h-3.5 w-3.5 text-slate-400" />
                                  <span>Owner:</span>
                                </span>
                                <span className="font-medium text-slate-800 dark:text-slate-200">
                                  {opp.owner}
                                </span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                  <span>Due Date:</span>
                                </span>
                                <span className="font-semibold text-slate-800 dark:text-slate-200">
                                  {opp.dueDate} {opp.dueTime ? `(${opp.dueTime})` : ""}
                                </span>
                              </div>

                              {opp.questionsDueDate && (
                                <div className="flex items-center justify-between">
                                  <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                                    <Clock className="h-3.5 w-3.5 text-amber-500" />
                                    <span>Q&A Deadline:</span>
                                  </span>
                                  <span className="font-medium text-amber-700 dark:text-amber-300">
                                    {opp.questionsDueDate}
                                  </span>
                                </div>
                              )}

                              {opp.requirementsSummary && (
                                <div className="flex items-center justify-between">
                                  <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                                    <Layers className="h-3.5 w-3.5 text-slate-400" />
                                    <span>Requirements:</span>
                                  </span>
                                  <span className="font-medium text-slate-700 dark:text-slate-300">
                                    {opp.requirementsSummary.confirmed}/
                                    {opp.requirementsSummary.total} Confirmed
                                  </span>
                                </div>
                              )}

                              {opp.documentPaths && opp.documentPaths.length > 0 && (
                                <div className="flex items-center justify-between">
                                  <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                                    <FileText className="h-3.5 w-3.5 text-slate-400" />
                                    <span>Attached Files:</span>
                                  </span>
                                  <span className="font-medium text-slate-700 dark:text-slate-300">
                                    {opp.documentPaths.length} documents
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Detailed Action Bar & Prominent Workspace Button */}
                            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2.5 dark:border-slate-700/60">
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenQualify(opp);
                                  }}
                                  className="btn-animate inline-flex items-center rounded-lg border border-teal-200 bg-teal-50/80 px-2.5 py-1 text-xs font-bold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-900/60 dark:bg-teal-950/40 dark:text-teal-300"
                                >
                                  {opp.decision
                                    ? `Decision: ${opp.decision.toUpperCase()}`
                                    : "Qualify Gate"}
                                </button>

                                {/* Dedicated Prominent Workspace Link */}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenWorkspace(opp);
                                  }}
                                  title="Open Opportunity Workspace"
                                  aria-label="Open Workspace"
                                  className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3 py-1 text-xs font-semibold text-white shadow-2xs hover:bg-[#238997]"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                  <span>Workspace</span>
                                </button>
                              </div>

                              <div className="flex items-center gap-1">
                                {/* Move Back Button */}
                                {opp.stage === "drafting" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "reviewing");
                                    }}
                                    title="Move back to Intake"
                                    className="btn-animate inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1.5 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <ArrowLeft className="h-3 w-3" />
                                  </button>
                                )}
                                {opp.stage === "in_review" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "drafting");
                                    }}
                                    title="Move back to Drafting"
                                    className="btn-animate inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1.5 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <ArrowLeft className="h-3 w-3" />
                                  </button>
                                )}
                                {opp.stage === "approved" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "in_review");
                                    }}
                                    title="Move back to Review"
                                    className="btn-animate inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1.5 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <ArrowLeft className="h-3 w-3" />
                                  </button>
                                )}
                                {opp.stage === "submitted" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "approved");
                                    }}
                                    title="Move back to Approved"
                                    className="btn-animate inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1.5 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <ArrowLeft className="h-3 w-3" />
                                  </button>
                                )}

                                {/* Move Forward Button */}
                                {opp.stage === "reviewing" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "drafting");
                                    }}
                                    title="Advance to drafting"
                                    className="btn-animate inline-flex items-center gap-1 rounded-lg bg-[#2ba5b5] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#238997]"
                                  >
                                    <span>Draft</span>
                                    <ArrowRight className="h-3 w-3" />
                                  </button>
                                )}

                                {opp.stage === "go" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "drafting");
                                    }}
                                    title="Start drafting"
                                    className="btn-animate inline-flex items-center gap-1 rounded-lg bg-teal-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-teal-700"
                                  >
                                    <span>Draft</span>
                                    <ArrowRight className="h-3 w-3" />
                                  </button>
                                )}

                                {opp.stage === "drafting" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "in_review");
                                    }}
                                    title="Advance to review"
                                    className="btn-animate inline-flex items-center gap-1 rounded-lg bg-purple-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-purple-700"
                                  >
                                    <span>Review</span>
                                    <ArrowRight className="h-3 w-3" />
                                  </button>
                                )}

                                {opp.stage === "in_review" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "approved");
                                    }}
                                    title="Approve"
                                    className="btn-animate inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-700"
                                  >
                                    <span>Approve</span>
                                    <CheckCircle2 className="h-3 w-3" />
                                  </button>
                                )}

                                {opp.stage === "approved" && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "submitted");
                                    }}
                                    title="Mark submitted"
                                    className="btn-animate inline-flex items-center gap-1 rounded-lg bg-cyan-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-cyan-700"
                                  >
                                    <span>Submit</span>
                                    <CheckCircle2 className="h-3 w-3" />
                                  </button>
                                )}

                                {["won", "lost", "no_go", "archived"].includes(opp.stage) && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      transition(opp, "drafting");
                                    }}
                                    title="Reopen opportunity back to drafting"
                                    className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                                  >
                                    <RotateCcw className="h-3 w-3 text-slate-400" />
                                    <span>Reopen</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </motion.div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </LayoutGroup>
    </div>
  );
};
